import Highcharts from 'highcharts';
import _HighchartsReact from 'highcharts-react-official';
import { Text, View, fromTheme, hooks, styled } from 'lib_ui-primitives';
import { createElement as rc, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Chart from './Chart';
const { useMemoizedObject } = hooks;
//@ts-ignore
const HighchartsReact = _HighchartsReact.default || _HighchartsReact;
HighchartsReact.displayName = 'HighchartsReact';

const Stack = styled(View).attrs({ name: 'PieChart' })`
    flex-direction: ${({ isWide }) => isWide ? 'row' : 'column'};
    height: 100%;
    width: 100%;
    align-items: ${({ isWide }) => isWide ? 'stretch' : 'center'};
    justify-content: ${({ isWide }) => isWide ? 'flex-start' : 'center'};
    position: relative;
    max-width: ${fromTheme('charts', 'maxWidth')};
`;

const ChartContainer = styled(View)`
    flex-grow: 1;
    flex-shrink: 1;
    width: auto;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CenteredChart = styled(View)`
    width: 100%;
    height: 100%;
    max-width: ${({ maxSize }) => maxSize}px;
    max-height: ${({ maxSize }) => maxSize}px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const LegendContainer = styled(View)`
    flex-direction: column;
    align-items: flex-start;
    padding: ${fromTheme('charts', 'padding')};
    z-index: 2;
    
    ${({ isWide, theme }) => isWide ? `
        position: relative;
        width: auto;
        justify-content: flex-end;
        padding-right: ${theme.charts.pie.paddingRight}px;
    ` : `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        align-items: flex-start;
        width: auto;
    `}
`;

const LegendItem = styled(View)`
    flex-direction: row;
    align-items: center;
    margin-top: ${fromTheme('charts', 'legend', 'itemSpacing')};
    cursor: pointer;
    user-select: none;
    pointer-events: auto;
    &:hover {
        opacity: ${({theme}) => theme.charts.legend.hoverOpacity};
    }
    
    ${({ isInside, theme }) => isInside && `
        justify-content: flex-start;
        margin-top: ${theme.charts.legend.insideMarginTop}px;
    `}
`;

const LegendDot = styled(View)`
    width: ${fromTheme('charts', 'legend', 'dotSize')};
    height: ${fromTheme('charts', 'legend', 'dotSize')};
    border-radius: ${fromTheme('charts', 'legend', 'dotRadius')};
    margin-right: ${fromTheme('charts', 'legend', 'itemGap')};
`;

const LegendText = styled(Text)`
    font-family: ${fromTheme('charts', 'legend', 'labelFont', 'fontFamily')};
    font-size: ${({ isInside, theme }) => isInside ? theme.charts.legend.insideLabelFont.fontSize : theme.charts.legend.labelFont.fontSize}px;
    font-weight: ${fromTheme('charts', 'legend', 'labelFont', 'fontWeight')};
    color: ${({ disabled, theme }) => disabled ? theme.disabledFontColor : theme.defaultFontColor};
    text-align: left;
`;

const EMPTY_ARRAY = [];
function Pie(props) {
    const { data = EMPTY_ARRAY } = props || { hNode: {} };

    const theme = useTheme();
    const charts = theme.charts;
    const chartColors = theme.chartColors;
    const memoizedData = useMemoizedObject(data);
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [hiddenIndices, setHiddenIndices] = useState(new Set());
    const [isWide, setIsWide] = useState(true);
    const [isLayoutReady, setIsLayoutReady] = useState(false);

    // Measure layout BEFORE the first paint
    useLayoutEffect(() => {
        if (containerRef.current) {
            const width = containerRef.current.offsetWidth;
            if (width > 0) {
                setIsWide(width >= 500);
                setIsLayoutReady(true);
            }
        }
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;
        
        const observer = new ResizeObserver(() => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                if (width > 0) {
                    setIsWide(width >= 500);
                    setIsLayoutReady(true);
                }
            }
            if (chartRef.current) {
                chartRef.current.reflow();
            }
        });
        
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.reflow();
        }
    }, [charts.pie.size, isWide, isLayoutReady]);

    const options = useMemo(() => {
        return {
            chart: {
                type: 'pie',
                backgroundColor: 'transparent',
                styledMode: true,
                spacing: charts.pie.spacing
            },
            title: { text: '' },
            tooltip: {
                pointFormat: '{series.name}: <b>{point.y}</b><br/>% of Total: <b>{point.percentage:.1f}%</b>'
            },
            colors: chartColors,
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: { enabled: false },
                    showInLegend: false,
                    innerSize: isWide ? '50%' : '65%',
                    borderWidth: 0,
                    center: ['50%', '50%'],
                    size: '100%',
                    borderRadius: 0,
                    inactiveOtherPoints: false,
                    states: {
                        hover: {
                            brightness: 0.2
                        }
                    }
                }
            },
            credits: { enabled: false },
            legend: { enabled: false },
            series: [{
                name: 'Count',
                colorByPoint: true,
                data: memoizedData.map((x, i) => ({
                    name: x.name || x._id?.title || 'None',
                    y: x.y ?? x.count,
                    color: charts.dataColors[i % charts.dataColors.length],
                    visible: !hiddenIndices.has(i)
                }))
            }]
        };
    }, [memoizedData, charts, chartColors, hiddenIndices, isWide]);

    const handleLegendClick = (index) => {
        setHiddenIndices(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const handleMouseEnter = (index) => {
        const chart = chartRef.current;
        if (chart && chart.series[0] && chart.series[0].points[index] && !hiddenIndices.has(index)) {
            const point = chart.series[0].points[index];
            // Trigger the hover state on the individual slice and show its tooltip
            point.setState('hover');
            chart.tooltip.refresh(point);
        }
    };

    const handleMouseLeave = (index) => {
        const chart = chartRef.current;
        if (chart && chart.series[0] && chart.series[0].points[index]) {
            chart.series[0].points[index].setState('');
            chart.tooltip.hide();
        }
    };

    // Render custom legend
    const legendItems = memoizedData.map((x, i) => {
        const isDisabled = hiddenIndices.has(i);
        return rc(LegendItem, { 
            key: i,
            isInside: !isWide,
            onClick: () => handleLegendClick(i),
            onMouseEnter: () => handleMouseEnter(i),
            onMouseLeave: () => handleMouseLeave(i)
        },
            rc(LegendDot, { 
                style: { 
                    backgroundColor: isDisabled ? theme.disabledFontColor : (charts.dataColors[i % charts.dataColors.length]),
                    opacity: isDisabled ? charts.legend.disabledOpacity : 1
                }
            }),
            rc(LegendText, { theme, disabled: isDisabled, isInside: !isWide }, 
                `${x.name || x._id?.title || 'None'} - ${x.y ?? x.count}`
            )
        );
    });

    return rc(
        Stack,
        { ref: containerRef, isWide },
        rc(LegendContainer, { isWide }, legendItems),
        rc(ChartContainer, { isWide },
            isLayoutReady && rc(CenteredChart, { maxSize: charts.pie.size },
                rc(HighchartsReact, {
                    highcharts: Highcharts,
                    options,
                    height: '100%',
                    width: '100%',
                    callback: (chart) => { chartRef.current = chart; }
                })
            )
        )
    );
}

function PieChart(props) {
    return Chart(Pie, props);
}

export default PieChart;
